/**
 * Currency Service
 *
 * Service for fetching exchange rates from the currencyRates microservice.
 */

import { get, type ApiResponse } from './api-client'

export interface CurrencyInfo {
  code: string
  name: string
  symbol: string
  decimalPlaces: number
  isActive: boolean
  isCrypto: boolean
  countryCodes?: string[]
}

export interface ExchangeRate {
  from: string
  to: string
  rate: number
  timestamp: string
  source?: string
}

export interface ExchangeRateResponse {
  rate: ExchangeRate
}

/**
 * Fetch the current exchange rate between two currencies.
 *
 * @param fromCurrency - Source currency code (e.g., "USD")
 * @param toCurrency - Target currency code (e.g., "INR")
 * @returns Exchange rate data
 */
/**
 * Map a rates API response onto an ExchangeRate ApiResponse, preserving the
 * existing success/error fallback semantics.
 */
function mapExchangeRateResponse(response: ApiResponse<ExchangeRateResponse>): ApiResponse<ExchangeRate> {
  // Check if we got data successfully (no error means success)
  if (response.data) {
    return {
      data: response.data.rate,
    }
  }

  // If there's an error, return it
  return {
    error: response.error ?? {
      code: 'CURRENCY_SERVICE_ERROR',
      message: 'Failed to fetch exchange rate',
    },
  }
}

export async function getExchangeRate(
  fromCurrency: string,
  toCurrency: string,
  force = false,
): Promise<ApiResponse<ExchangeRate>> {
  // Normalise input — strip whitespace and upper-case. A bad caller (or
  // half-loaded form state) used to send "/rates/   /INR" and trigger a 500
  // on the server. Treat empty/whitespace as invalid up front so the request
  // is never made.
  const from = String(fromCurrency ?? '').trim().toUpperCase()
  const to   = String(toCurrency   ?? '').trim().toUpperCase()
  if (!from || !to) {
    return {
      error: {
        code: 'INVALID_CURRENCY',
        message: 'Currency codes must be non-empty',
      },
    }
  }

  // If same currency, return rate of 1
  if (from === to) {
    return {
      data: {
        from, to,
        rate: 1.0,
        timestamp: new Date().toISOString(),
        source: 'internal',
      },
    }
  }

  try {
    const url = force
      ? `/rates/${from}/${to}?force=true`
      : `/rates/${from}/${to}`
    const response = await get<ExchangeRateResponse>(url)
    return mapExchangeRateResponse(response)
  } catch (error: unknown) {
    return {
      error: {
        code: 'CURRENCY_SERVICE_ERROR',
        message: error instanceof Error ? error.message : 'Failed to fetch exchange rate',
      },
    }
  }
}

/**
 * Fetch exchange rates for multiple currency pairs.
 *
 * @param pairs - Array of currency pairs [{ from: "USD", to: "INR" }, ...]
 * @returns Map of currency pairs to rates
 */
export async function getExchangeRates(
  pairs: Array<{ from: string; to: string }>
): Promise<ApiResponse<Map<string, number>>> {
  const rates = new Map<string, number>()
  const errors: string[] = []

  for (const pair of pairs) {
    const result = await getExchangeRate(pair.from, pair.to)
    if (result.data) {
      const key = `${pair.from}_${pair.to}`
      rates.set(key, result.data.rate)
    } else {
      errors.push(`${pair.from}/${pair.to}: ${result.error?.message}`)
    }
  }

  if (errors.length > 0) {
    return {
      error: {
        code: 'PARTIAL_FETCH_ERROR',
        message: `Failed to fetch some rates: ${errors.join(', ')}`,
      },
    }
  }

  return {
    data: rates,
  }
}

/**
 * Fetch all available currencies from the CurrencyRates microservice.
 */
export async function listCurrencies(): Promise<ApiResponse<CurrencyInfo[]>> {
  try {
    const response = await get<CurrencyInfo[]>('/currencies')
    if (response.data) {
      return { data: response.data }
    }
    return {
      error: response.error ?? {
        code: 'CURRENCY_SERVICE_ERROR',
        message: 'Failed to fetch currencies',
      },
    }
  } catch (error: unknown) {
    return {
      error: {
        code: 'CURRENCY_SERVICE_ERROR',
        message: error instanceof Error ? error.message : 'Failed to fetch currencies',
      },
    }
  }
}

/**
 * Convert an amount from one currency to another.
 *
 * @param amount - Amount in source currency
 * @param fromCurrency - Source currency code
 * @param toCurrency - Target currency code
 * @returns Converted amount
 */
export async function convertCurrency(
  amount: number,
  fromCurrency: string,
  toCurrency: string
): Promise<ApiResponse<number>> {
  const rateResult = await getExchangeRate(fromCurrency, toCurrency)

  if (!rateResult.data) {
    return {
      error: rateResult.error,
    }
  }

  return {
    data: amount * rateResult.data.rate,
  }
}
